// =============================================================================
// src/views/impostometro/impostometro-dom.ts — Helpers de DOM da view Impostometro.
//
// Proveniencia: assets/unified-app.js linhas 821-856 (var UI). O scaffold define o
//   TIPO UiHelper em src/app/types.ts mas nao ha implementacao compartilhada de
//   dono unico ainda; portamos APENAS os 3 helpers que a Section.impostometro usa
//   (qsa / setActive / bindOnce), escopados a esta view para evitar duplicar a
//   logica inline e manter os nomes greppaveis. Comportamento identico ao original.
// =============================================================================

/** Chave de flag bindOnce gravada no elemento (assets/unified-app.js 827). */
type BoundFlagKey = `__bound_${string}_${string}`;

/**
 * querySelectorAll como array (porte de UI.qsa).
 * @param selector seletor CSS.
 * @param scope raiz da busca; default `document`.
 * @returns elementos casados como Element[] (snapshot, nao NodeList vivo).
 */
export function queryImpostometroTabs(
  selector: string,
  scope: ParentNode = document,
): readonly Element[] {
  return Array.prototype.slice.call(scope.querySelectorAll(selector));
}

/**
 * Marca um item como `.active` e remove de todos os irmaos (porte de UI.setActive).
 * @param tabs conjunto de tabs irmaos.
 * @param activeTab tab que deve receber `.active`.
 */
export function setActiveTab(
  tabs: readonly Element[],
  activeTab: Element,
): void {
  tabs.forEach((tab) => tab.classList.toggle('active', tab === activeTab));
}

/**
 * Registra um listener uma unica vez por (evento, chave), gravando um flag no
 * proprio elemento — porte fiel de UI.bindOnce (assets/unified-app.js 825-831).
 * Reproduz o no-op silencioso quando o elemento e nulo, em vez de lancar erro,
 * para manter o comportamento legado (ver notesAndDeviations no resumo).
 * @param target elemento alvo (pode ser null no fluxo legado).
 * @param eventName nome do evento DOM (ex.: 'click').
 * @param bindKey chave logica do binding (ex.: 'impostometro-tributo').
 * @param listener callback do evento.
 */
export function bindTabListenerOnce(
  target: Element | null,
  eventName: string,
  bindKey: string,
  listener: (this: Element, event: Event) => void,
): void {
  if (!target) return;
  const flag: BoundFlagKey = `__bound_${eventName}_${bindKey}`;
  const flagged = target as Element & Record<BoundFlagKey, boolean | undefined>;
  if (flagged[flag]) return;
  flagged[flag] = true;
  target.addEventListener(eventName, listener);
}
